<script setup lang="ts">
import { ref, onMounted, onUnmounted } from 'vue';
import { bus } from '@core/events/eventSystem';
import constants from '@core/utils/constants';

const showMilestones = ref(false);
const milestones = ref([]);
const loading = ref(true);
const claimLoading = ref<string | null>(null);
const claimedRewards = ref<any[]>([]);

const claimDialog = ref(null);

// Pourcentage de progression (0–100), clampé
const getPercentage = (current: number, target: number) => {
  if (target <= 0) return 0;
  return Math.min(100, Math.round((current / target) * 100));
};

function claimMilestone(item: any) {
  if (claimLoading.value) return;
  claimLoading.value = item.id;
  bus.emit(constants.EVENTS.REWARDS.CLAIM, { id: item.id, ...item });
}

onMounted(() => {
  bus.emit(constants.EVENTS.REWARDS.LOAD_MILESTONES);

  const handleMilestonesLoaded = (data: any[]) => {
    milestones.value = Array.isArray(data) ? data : [];
    loading.value = false;
  };

  const handleClaimed = (data: any) => {
    claimLoading.value = null;
    claimedRewards.value = Array.isArray(data?.rewards) ? data.rewards : [];
    claimDialog.value?.show();
  };

  const handleNotClaimed = () => {
    claimLoading.value = null;
    bus.emit(constants.EVENTS.REWARDS.LOAD_MILESTONES);
  };

  bus.on(constants.EVENTS.REWARDS.MILESTONES_LOADED, handleMilestonesLoaded);
  bus.on(constants.EVENTS.REWARDS.CLAIMED, handleClaimed);
  bus.on(constants.EVENTS.REWARDS.NOT_CLAIMED, handleNotClaimed);

  onUnmounted(() => {
    bus.off(constants.EVENTS.REWARDS.MILESTONES_LOADED, handleMilestonesLoaded);
    bus.off(constants.EVENTS.REWARDS.CLAIMED, handleClaimed);
    bus.off(constants.EVENTS.REWARDS.NOT_CLAIMED, handleNotClaimed);
  });
});
</script>

<template>
  <!-- Backdrop -->
  <div
    class="w-full h-full fixed top-0 left-0 z-40 bg-black/20"
    @click="showMilestones = false"
    v-if="showMilestones"
  ></div>

  <!-- Panel principal -->
  <div
    class="fixed left-full z-50 w-[74vw] max-w-md h-[70vh] bottom-[18vh] transition-transform duration-300 ease-in-out"
    :class="{ 'translate-x-[-100%]': showMilestones }"
  >
    <div
      class="bg-orange-500 shadow-2xl shadow-orange-500/30 border border-r-0 border-white w-full pr-1 rounded-l-4xl h-full relative flex flex-col"
    >
      <div class="overflow-y-auto h-full p-5 space-y-4">
        <!-- En-tête -->
        <header class="mb-4">
          <h1 class="text-2xl font-bold text-white drop-shadow-xs">
            Objectifs Rapidoo
          </h1>
          <p class="text-xs text-orange-100 font-medium">
            Atteignez les objectifs pour débloquer des récompenses Rapidoo
          </p>
        </header>

        <!-- Liste des Milestones -->
        <div class="flex flex-col gap-4">
          <div
            v-for="item in milestones"
            :key="item.id"
            class="relative p-4 rounded-2xl border-2 transition-all duration-300"
            :class="
              item.completed
                ? 'bg-white border-emerald-400'
                : 'bg-white/95 border-white shadow-lg'
            "
          >
            <div class="flex justify-between items-start mb-2">
              <h3 class="font-bold text-slate-800 text-lg">{{ item.name }}</h3>
              <span
                v-if="item.completed"
                class="text-xs font-bold text-emerald-600 bg-emerald-100 px-2 py-0.5 rounded-full"
              >✓&nbsp;ACCOMPLI</span>
            </div>

            <p class="text-slate-600 text-xs mb-3">{{ item.description }}</p>

            <!-- Section Affichage des Récompenses (XP, Points, Badges, Coupons, etc.) -->
            <div class="flex flex-wrap items-center gap-2 mb-3" v-if="item.completed">
              <!-- XP -->
              <div
                v-if="item.rewardXp"
                class="bg-orange-100 text-orange-700 px-2 py-1 rounded-lg text-[10px] font-bold"
              >
                ✨ {{ item.rewardXp }} XP
              </div>

              <!-- Points -->
              <div
                v-if="item.rewardPoints"
                class="bg-blue-100 text-blue-700 px-2 py-1 rounded-lg text-[10px] font-bold"
              >
                💎 {{ item.rewardPoints }} pts
              </div>

              <!-- Dynamic Rewards (Badges, Coupons, Custom) -->
              <template v-for="(reward, idx) in item.rewards" :key="idx">
                <!-- Badge (Image) -->
                <div
                  v-if="reward.type === 'badge'"
                  class="flex items-center gap-1 bg-purple-100 text-purple-700 text-xs font-bold px-2 py-1 rounded-lg border border-purple-200"
                >
                  <img :src="reward.value" class="w-4 h-4 object-contain" alt="Badge" />
                  <span>Badge</span>
                </div>

                <!-- Coupon -->
                <div
                  v-else-if="reward.type === 'coupon'"
                  class="flex items-center gap-1 bg-emerald-100 text-emerald-800 text-xs font-bold px-2 py-1 rounded-lg border border-emerald-200"
                >
                  🏷️ 
                  <span v-if="typeof reward.value === 'object'">
                    -{{ reward.value.discount }}{{ reward.value.type === 'percentage' ? '%' : '€' }} ({{ reward.value.code }})
                  </span>
                  <span v-else>{{ reward.value }}</span>
                </div>

                <!-- Generic Fallback Reward -->
                <div
                  v-else
                  class="flex items-center gap-1 bg-slate-100 text-slate-700 text-xs font-bold px-2 py-1 rounded-lg border border-slate-200"
                >
                  🎁 {{ typeof reward.value === 'object' ? (reward.value.name || reward.type) : reward.value }}
                </div>
              </template>
            </div>
            <!-- Action Réclamer -->
            <div class="flex mb-3" v-if="item.completed && !item.claimed">
              <button
                class="buttonComponent w-full bg-primary text-white drop-shadow-lg hover:drop-shadow-xl transform hover:scale-105 transition-all duration-300 font-bold px-5 py-2 rounded-full text-sm border-2 border-white/50 flex items-center justify-center gap-2"
                :class="{
                  'opacity-60 pointer-events-none': claimLoading === item.id
                }"
                @click.stop="claimMilestone(item)"
              >
                <span
                  v-if="claimLoading === item.id"
                  class="loading loading-spinner loading-xs"
                ></span>
                <span>Réclamer</span>
              </button>
            </div>

            <!-- Progression -->
            <div
              class="flex justify-between items-center text-[11px] font-medium text-slate-500 mb-1"
            >
              <span>Progression</span>
              <span
                >{{ Math.min(item.currentValue, item.targetValue) }} /
                {{ item.targetValue }}</span
              >
            </div>
            <div class="h-3 bg-slate-200 rounded-full overflow-hidden">
              <div
                class="h-full transition-all duration-500"
                :class="item.completed ? 'bg-emerald-500' : 'bg-orange-500'"
                :style="{
                  width: `${getPercentage(item.currentValue, item.targetValue)}%`
                }"
              ></div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Bouton Toggle -->
    <button
      @click="showMilestones = !showMilestones"
      class="w-16 h-16 -mr-[1px] rounded-l-full bg-orange-500 border border-white border-r-0 absolute right-full top-[calc(80%-10rem)] text-white flex justify-center items-center"
      :class="{ 'border-transparent!': showMilestones }"
    >
      <ColorImg
        src="/images/all/badge-premium.png"
        class="bg-white w-8 h-8"
        alt=""
      />
    </button>
  </div>

  <!-- Modale Post-Claim -->
  <DialogComponent
    ref="claimDialog"
    dialogId="milestone-claimed-dialog"
    type="modal"
  >
    <div class="flex flex-col items-center gap-5 p-6">
      <div
        class="w-20 h-20 rounded-full bg-gradient-to-br from-primary to-orange-400 flex items-center justify-center text-4xl shadow-lg shadow-orange-300"
      >
        🎉
      </div>

      <div class="text-center">
        <h2 class="text-2xl font-bold text-slate-800">Récompense obtenue !</h2>
        <p class="text-slate-500 text-sm mt-1">Voici ce que vous avez gagné</p>
      </div>

      <!-- Detail des Récompenses Reçues -->
      <div class="w-full flex flex-col gap-2">
        <div
          v-for="(reward, idx) in claimedRewards"
          :key="idx"
          class="flex items-center gap-3 bg-purple-50 border border-purple-200 rounded-2xl px-4 py-3"
        >
          <!-- Contenu Badge -->
          <template v-if="reward.type === 'badge'">
            <img :src="reward.value" class="w-12 h-12 object-contain" alt="Badge d'accomplissement" />
            <div>
              <div class="text-xs text-purple-500 font-semibold uppercase tracking-wider">Badge débloqué</div>
              <div class="text-sm font-bold text-purple-900">Nouveau Badge</div>
            </div>
          </template>

          <!-- Contenu Coupon -->
          <template v-else-if="reward.type === 'coupon'">
            <span class="text-2xl">🏷️</span>
            <div>
              <div class="text-xs text-purple-500 font-semibold uppercase tracking-wider">
                Coupon de réduction
              </div>
              <div class="text-base font-bold text-purple-900" v-if="typeof reward.value === 'object'">
                Code : <span class="bg-purple-200 px-2 py-0.5 rounded text-purple-900 font-mono">{{ reward.value.code }}</span>
                <span class="text-xs block text-slate-600 font-normal mt-0.5">
                  Réduction : {{ reward.value.discount }}{{ reward.value.type === 'percentage' ? '%' : '€' }}
                </span>
              </div>
              <div class="text-base font-bold text-purple-900" v-else>
                {{ reward.value }}
              </div>
            </div>
          </template>

          <!-- Contenu Générique (XP, points, objets, etc.) -->
          <template v-else>
            <span class="text-2xl">🎁</span>
            <div>
              <div class="text-xs text-purple-500 font-semibold uppercase tracking-wider">{{ reward.type }}</div>
              <div class="text-base font-bold text-purple-900">
                {{ typeof reward.value === 'object' ? JSON.stringify(reward.value) : reward.value }}
              </div>
            </div>
          </template>
        </div>

        <div
          v-if="!claimedRewards.length"
          class="text-center text-slate-400 text-sm py-2"
        >
          Récompense accordée par la plateforme ✨
        </div>
      </div>

      <button
        class="buttonComponent w-full bg-gradient-to-r from-primary to-orange-500 text-white font-bold py-3 rounded-full text-base border-2 border-white/50 shadow-lg shadow-orange-300"
        @click="claimDialog?.hide()"
      >
        Super, merci !
      </button>
    </div>
  </DialogComponent>
</template>